import { useState } from "react";
import { useQuery } from "react-query";
import { Badge, Button } from "flowbite-react";
import {
  MdContentCopy,
  MdOutlineEventNote,
  MdOutlineReceiptLong,
  MdOutlinePeopleAlt,
  MdOutlineDesignServices,
  MdOutlineInventory2,
  MdOutlineCategory,
  MdOutlineDescription,
  MdStarRate,
  MdOutlinePayments,
} from "react-icons/md";
import { FaWhatsapp, FaEdit } from "react-icons/fa";
import { MdLogin } from "react-icons/md";

import { APP_LOGIN_URL, CLIENTS, PROFESSIONALS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import DrawerLoading from "../DrawerLoading";
import { LifetimeAppointmentsChart } from "../customers/portfolioCharts";
import {
  buildWhatsappLink,
  describeExpiration,
  describeLastActivity,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  getAvatarColor,
  getHealthMeta,
  getInitials,
  getPlanMeta,
  getStatusMeta,
} from "../customers/customerHelpers";

const TABS = [
  { key: "overview", label: "Visão geral" },
  { key: "usage", label: "Uso" },
  { key: "finance", label: "Financeiro" },
  { key: "team", label: "Equipe" },
];

const PERMISSION_LABELS = {
  ADMIN: "Administrador",
  USER_CALENDARS: "Secretária",
  USER: "Usuário comum",
};


/** Excluído tem precedência sobre inativo — é o estado mais definitivo. */
const getProfessionalState = (professional) => {
  if (professional.isExcluded) {
    return { label: "Excluído", color: "failure" };
  }

  if (!professional.isActive) {
    return { label: "Inativo", color: "warning" };
  }

  return { label: "Ativo", color: "success" };
};

const getStatusCount = (collection, statusId) =>
  (Array.isArray(collection) ? collection : []).find(
    (item) => item.statusId === statusId
  )?.count || 0;

const getProviderLabel = (provider) => {
  if (provider === "mercado_pago") return "Mercado Pago";
  if (provider === "asaas") return "Asaas";
  return "Não informado";
};

const getPaymentStatusColor = (status) => {
  const normalized = String(status || "").toLowerCase();

  if (
    normalized.includes("approved") ||
    normalized.includes("paid") ||
    normalized.includes("receb") ||
    normalized.includes("receiv") ||
    normalized.includes("confirm")
  ) {
    return "success";
  }

  if (
    normalized.includes("pending") ||
    normalized.includes("pendente") ||
    normalized.includes("processing")
  ) {
    return "warning";
  }

  if (
    normalized.includes("cancel") ||
    normalized.includes("rejected") ||
    normalized.includes("failed")
  ) {
    return "failure";
  }

  return "gray";
};

const MetricTile = ({ icon: Icon, label, value, hint }) => (
  <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
    <div className="flex items-center gap-2 text-slate-400">
      <Icon size={16} />
      <span className="text-[10px] font-semibold uppercase tracking-[0.16em]">
        {label}
      </span>
    </div>
    <p className="mt-1 text-xl font-bold leading-tight text-slate-900">{value}</p>
    {hint ? <p className="text-[11px] text-slate-500">{hint}</p> : null}
  </div>
);

/** Mini gráfico de barras em CSS — evita puxar biblioteca só para 6 colunas. */
const UsageChart = ({ series }) => {
  const max = Math.max(
    1,
    ...series.map((item) => Math.max(item.appointments, item.commands))
  );

  return (
    <div className="flex items-end justify-between gap-3">
      {series.map((item) => (
        <div key={item.month} className="flex flex-1 flex-col items-center gap-1">
          <div className="flex h-28 w-full items-end justify-center gap-1">
            <div
              className="w-3 rounded-t bg-pink-400"
              style={{ height: `${(item.appointments / max) * 100}%` }}
              title={`${item.appointments} agendamentos`}
            />
            <div
              className="w-3 rounded-t bg-sky-400"
              style={{ height: `${(item.commands / max) * 100}%` }}
              title={`${item.commands} comandas`}
            />
          </div>
          <span className="text-[10px] text-slate-500">{item.label}</span>
          <span className="text-[10px] font-semibold text-slate-700">
            {formatNumber(item.appointments)}
          </span>
        </div>
      ))}
    </div>
  );
};

const CompanyDetailsForm = ({ company, onEditProfessional }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [pendingProfessionalId, setPendingProfessionalId] = useState(null);
  const [professionalError, setProfessionalError] = useState("");

  /** Cadastro completo do profissional, para abrir o formulário de edição. */
  const loadProfessional = async (professionalId) => {
    setProfessionalError("");
    setPendingProfessionalId(professionalId);

    try {
      const { data: response } = await axios.get(
        `${PROFESSIONALS.ONE}/${professionalId}`
      );

      return response.data;
    } catch (requestError) {
      setProfessionalError(
        "Não foi possível carregar os dados desse profissional."
      );
      return null;
    } finally {
      setPendingProfessionalId(null);
    }
  };

  const handleEditProfessional = async (professionalId) => {
    const payload = await loadProfessional(professionalId);
    if (!payload?.professional) return;

    // O formulário lê `imageUrl` no submit; este endpoint não devolve o campo.
    onEditProfessional({
      ...payload.professional,
      imageUrl: payload.professional.imageUrl ?? "",
    });
  };

  /*
    Personificar tem rota própria (POST), separada da leitura do cadastro: ela
    cria a sessão de suporte e registra na trilha de auditoria quem a abriu.
    Só ADMIN — para gerente de contas o servidor responde 403, e a mensagem
    dele é o que aparece na tela.
  */
  const handleImpersonateProfessional = async (professionalId) => {
    setProfessionalError("");
    setPendingProfessionalId(professionalId);

    try {
      const { data: response } = await axios.post(
        `${PROFESSIONALS.IMPERSONATE}/${professionalId}/impersonate`
      );

      window.open(
        `${APP_LOGIN_URL}?token=${response.data.token}`,
        "_blank",
        "noopener"
      );
    } catch (requestError) {
      setProfessionalError(
        requestError.response?.data?.message ||
          "Não foi possível abrir a sessão de suporte."
      );
    } finally {
      setPendingProfessionalId(null);
    }
  };

  const { isLoading, error, data: details } = useQuery(
    ["companyDetails", company?.id],
    async () => {
      const { data: response } = await axios.get(
        `${CLIENTS.ONE}/${company.id}`
      );

      return response.data;
    },
    { enabled: Boolean(company?.id) }
  );

  const record = details?.company ?? company ?? {};
  const metrics = details?.metrics ?? {};
  const statusMeta = getStatusMeta(record.status);
  const healthMeta = getHealthMeta(record.health);
  const planMeta = getPlanMeta(record.planId);
  const whatsappLink = buildWhatsappLink(record.phoneNumber);

  const totalAppointments =
    getStatusCount(details?.appointmentsCount, 0) +
    getStatusCount(details?.appointmentsCount, 1);
  const totalCommands =
    getStatusCount(details?.commandsCount, 0) +
    getStatusCount(details?.commandsCount, 1);

  const paymentHistory = details?.paymentHistory ?? [];
  const paymentSummary = details?.paymentHistorySummary ?? {
    approvedCount: 0,
    totalAmount: 0,
    mercadoPagoAmount: 0,
    asaasAmount: 0,
  };
  const planAdditionals = details?.planAdditionals ?? [];
  const professionals = details?.professionals ?? [];
  const usageSeries = details?.usageSeries ?? [];
  const lifetimeSeries = details?.lifetimeSeries ?? [];
  const teamCounts = professionals.reduce(
    (acc, professional) => {
      if (professional.isExcluded) acc.excluded += 1;
      else if (professional.isActive) acc.active += 1;
      else acc.inactive += 1;

      return acc;
    },
    { active: 0, inactive: 0, excluded: 0 }
  );
  const timeline = details?.timeline ?? [];

  return (
    <>
      <div className="min-w-[320px] space-y-4">
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <span
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-bold ${getAvatarColor(
                  record.id
                )}`}
              >
                {getInitials(record.name)}
              </span>
              <div>
                <p className="text-xs text-slate-500">
                  Cadastrado em {formatDate(record.createdAt)}
                  {record.daysSinceRegistration !== null &&
                  record.daysSinceRegistration !== undefined
                    ? ` (${record.daysSinceRegistration} dias)`
                    : ""}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Badge color={planMeta.color}>{planMeta.label}</Badge>
                  <Badge color={statusMeta.color}>{statusMeta.label}</Badge>
                  <Badge color={healthMeta.color} title={healthMeta.hint}>
                    {healthMeta.label}
                  </Badge>
                  {record.isOverSeatLimit ? (
                    <Badge color="failure">Acima do limite de usuários</Badge>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start gap-2 md:items-end">
              {whatsappLink ? (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:underline"
                >
                  <FaWhatsapp size={14} />
                  {record.phoneNumber}
                </a>
              ) : null}
              <button
                type="button"
                onClick={() =>
                  record.email && navigator.clipboard?.writeText(record.email)
                }
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"
                title="Copiar e-mail"
              >
                <MdContentCopy size={12} />
                {record.email || "sem e-mail"}
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Plano
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {formatMoney(record.monthlyValue)}/mês
              </p>
              <p className="text-[11px] text-slate-500">
                {record.cupom ? `Cupom ${record.cupom}` : "Sem cupom"}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Vigência
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {formatDate(record.planExpirationDate)}
              </p>
              <p className="text-[11px] text-slate-500">
                {describeExpiration(record.daysToExpire)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Última atividade
              </p>
              <p className="text-sm font-semibold text-slate-900">
                {describeLastActivity(
                  record.lastActivityAt,
                  record.daysSinceLastActivity
                )}
              </p>
              <p className="text-[11px] text-slate-500">
                {record.lastActivityAt
                  ? formatDateTime(record.lastActivityAt)
                  : "nenhum registro"}
              </p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Integração
              </p>
              <p className="truncate text-sm font-semibold text-slate-900">
                {record.customer_id || "sem ID Asaas"}
              </p>
              <p className="text-[11px] text-slate-500">
                Lembretes {record.isActiveAutomaticReminder ? "ativos" : "inativos"}
              </p>
            </div>
          </div>
        </section>

        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${
                activeTab === tab.key
                  ? "border-pink-500 bg-pink-50 text-pink-600"
                  : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "overview" ? (
          <section className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetricTile
                icon={MdOutlineEventNote}
                label="Agendamentos"
                value={formatNumber(totalAppointments)}
                hint={`${formatNumber(
                  details?.appointmentsThisMonth
                )} neste mês · ${formatNumber(
                  metrics.appointmentsLast30Days
                )} em 30d`}
              />
              <MetricTile
                icon={MdOutlineReceiptLong}
                label="Comandas"
                value={formatNumber(totalCommands)}
                hint={`${formatMoney(metrics.revenueLast30Days)} faturados em 30d`}
              />
              <MetricTile
                icon={MdOutlinePeopleAlt}
                label="Clientes finais"
                value={formatNumber(details?.customersCount)}
                hint="Base de clientes do salão"
              />
              <MetricTile
                icon={MdOutlinePeopleAlt}
                label="Profissionais"
                value={`${formatNumber(metrics.professionalCount)}/${formatNumber(
                  record.seatLimit
                )}`}
                hint={`${formatNumber(
                  metrics.activeProfessionalCount
                )} ativos no momento`}
              />
              <MetricTile
                icon={MdOutlineDesignServices}
                label="Serviços"
                value={formatNumber(details?.servicesCount)}
              />
              <MetricTile
                icon={MdOutlineInventory2}
                label="Produtos"
                value={formatNumber(details?.productsCount)}
              />
              <MetricTile
                icon={MdOutlineCategory}
                label="Categorias"
                value={formatNumber(details?.categoriesCount)}
              />
              <MetricTile
                icon={MdOutlineDescription}
                label="Fichas"
                value={formatNumber(details?.recordsCount)}
              />
              <MetricTile
                icon={MdStarRate}
                label="Avaliações"
                value={
                  details?.reviews?.total
                    ? `${details.reviews.average} ★`
                    : "sem avaliações"
                }
                hint={`${formatNumber(details?.reviews?.total)} recebidas`}
              />
              <MetricTile
                icon={MdOutlinePayments}
                label="Total pago"
                value={formatMoney(metrics.totalPaid)}
                hint={
                  metrics.lastPaymentAt
                    ? `último em ${formatDate(metrics.lastPaymentAt)}`
                    : "sem pagamentos aprovados"
                }
              />
            </div>

            <LifetimeAppointmentsChart series={lifetimeSeries} />

            <div
              className={`rounded-2xl border px-4 py-3 text-sm ${
                record.health === "ACTIVE"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : record.health === "AT_RISK"
                    ? "border-amber-200 bg-amber-50 text-amber-800"
                    : "border-slate-200 bg-slate-50 text-slate-700"
              }`}
            >
              <strong className="block">{healthMeta.label}</strong>
              <span className="text-xs">{healthMeta.hint}</span>
            </div>
          </section>
        ) : null}

        {activeTab === "usage" ? (
          <section className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-800">
                  Uso nos últimos 6 meses
                </h3>
                <div className="flex items-center gap-3 text-[11px] text-slate-500">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-pink-400" />
                    Agendamentos
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-sky-400" />
                    Comandas
                  </span>
                </div>
              </div>
              <div className="mt-4">
                {usageSeries.length ? (
                  <UsageChart series={usageSeries} />
                ) : (
                  <p className="text-sm text-slate-500">Sem uso registrado.</p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">
                Atividade recente
              </h3>
              <div className="mt-3 space-y-2">
                {timeline.length ? (
                  timeline.map((event) => (
                    <div
                      key={`${event.type}-${event.id}`}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">
                          {event.title}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                          {event.description}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-slate-500">
                        {formatDateTime(event.createdAt)}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    Nenhuma movimentação registrada.
                  </p>
                )}
              </div>
            </div>
          </section>
        ) : null}

        {activeTab === "finance" ? (
          <section className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetricTile
                icon={MdOutlinePayments}
                label="Aprovados"
                value={formatNumber(paymentSummary.approvedCount)}
              />
              <MetricTile
                icon={MdOutlinePayments}
                label="Total aprovado"
                value={formatMoney(paymentSummary.totalAmount)}
              />
              <MetricTile
                icon={MdOutlinePayments}
                label="Asaas"
                value={formatMoney(paymentSummary.asaasAmount)}
              />
              <MetricTile
                icon={MdOutlinePayments}
                label="Mercado Pago"
                value={formatMoney(paymentSummary.mercadoPagoAmount)}
              />
            </div>

            {planAdditionals.length ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-800">
                  Adicionais contratados ({planAdditionals.length})
                </h3>
                <div className="mt-3 space-y-2">
                  {planAdditionals.map((additional) => (
                    <div
                      key={`${additional.name}-${additional.createdAt}`}
                      className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm"
                    >
                      <span className="text-slate-800">{additional.name}</span>
                      <span className="text-slate-600">
                        {formatMoney(additional.price)} ·{" "}
                        {formatDate(additional.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">
                Histórico de pagamentos ({paymentHistory.length})
              </h3>
              <div className="mt-3 space-y-2">
                {paymentHistory.length ? (
                  paymentHistory.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex flex-col gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 md:flex-row md:items-center md:justify-between"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          #{payment.id} · {getProviderLabel(payment.provider)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {payment.paidAt ? formatDateTime(payment.paidAt) : "Sem data"}
                          {payment.billingType ? ` · ${payment.billingType}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge color={getPaymentStatusColor(payment.status)}>
                          {payment.status || "Não informado"}
                        </Badge>
                        <span className="text-sm font-semibold text-slate-900">
                          {formatMoney(payment.value)}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">
                    Nenhum pagamento registrado para este cliente.
                  </p>
                )}
              </div>
            </div>
          </section>
        ) : null}

        {activeTab === "team" ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-slate-800">
                Profissionais ({professionals.length})
              </h3>
              <span className="text-xs text-slate-500">
                {formatNumber(teamCounts.active)} ativos ·{" "}
                {formatNumber(teamCounts.inactive)} inativos ·{" "}
                {formatNumber(teamCounts.excluded)} excluídos · limite{" "}
                {formatNumber(record.seatLimit)}
              </span>
            </div>

            {professionalError ? (
              <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {professionalError}
              </p>
            ) : null}

            <div className="mt-3 space-y-2">
              {professionals.length ? (
                professionals.map((professional) => {
                  const state = getProfessionalState(professional);
                  const isPending = pendingProfessionalId === professional.id;

                  return (
                    <div
                      key={professional.id ?? professional.email}
                      className={`flex flex-col gap-2 rounded-xl border px-3 py-2 md:flex-row md:items-center md:justify-between ${
                        professional.isExcluded
                          ? "border-red-100 bg-red-50/50"
                          : "border-slate-100 bg-slate-50"
                      }`}
                    >
                      <div className="min-w-0">
                        <p
                          className={`truncate text-sm font-semibold ${
                            professional.isExcluded
                              ? "text-slate-500 line-through"
                              : "text-slate-900"
                          }`}
                        >
                          {professional.name}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                          {professional.email}
                          {professional.phoneNumber
                            ? ` · ${professional.phoneNumber}`
                            : ""}
                        </p>
                      </div>

                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <Badge color={state.color}>{state.label}</Badge>
                        <Badge
                          color={
                            professional.permissionId === "ADMIN"
                              ? "purple"
                              : "gray"
                          }
                        >
                          {PERMISSION_LABELS[professional.permissionId] ||
                            "Usuário comum"}
                        </Badge>

                        {professional.id ? (
                          <>
                            {typeof onEditProfessional === "function" ? (
                              <Button
                                size="xs"
                                color="light"
                                disabled={isPending}
                                onClick={() =>
                                  handleEditProfessional(professional.id)
                                }
                                title="Editar profissional"
                              >
                                <FaEdit />
                              </Button>
                            ) : null}
                            <Button
                              size="xs"
                              color="light"
                              disabled={isPending}
                              onClick={() =>
                                handleImpersonateProfessional(professional.id)
                              }
                              title="Personificar (abrir o app como este profissional)"
                            >
                              <MdLogin />
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-sm text-slate-500">
                  Nenhum profissional cadastrado.
                </p>
              )}
            </div>
          </section>
        ) : null}

        {error ? (
          <p className="text-center text-sm text-red-600">
            Falha ao carregar os detalhes da empresa.
          </p>
        ) : null}
      </div>

      {isLoading ? <DrawerLoading /> : null}
    </>
  );
};

export default CompanyDetailsForm;
